import { useEffect, useState } from 'react';
import { Sheet } from 'react-modal-sheet';
import { t } from 'i18next';
import { useColorMode } from '@chakra-ui/react';
import { Button, ButtonToggle } from '@peacecover/component';
import useUserQuery from '../../../presentation/hooks/queries/useUserQuery';
import { useGetBettieProducts } from '../../../domains/bettie/ui/hooks/useGetBettieProducts';
import { useGetUserIdentities } from '../../../domains/user/ui/hooks/useGetUserIdentities';
import { openNewTab } from '../../../utils/UIUtils';
import BettieAgreementSection from '../../../presentation/chat/components/BettieAgreementSection';
import { BettieGradientColor } from '../../../constants/BettieGradientColor';
import { ReactComponent as WhiteCheckCircle } from '../../../icons/white.check.circle.line.svg';
import {
  isBillingBuyerInfoComplete,
  loadBillingBuyerInfo
} from '../../../utils/billingBuyerInfoStorage';
import type { components } from '../../../types/schema';

type BettieProduct = components['schemas']['ApiV1BettieProduct'];

interface BettieCreateRoomTicketBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const BettieCreateRoomTicketBottomSheet = ({
  isOpen,
  onClose
}: BettieCreateRoomTicketBottomSheetProps) => {
  const { colorMode } = useColorMode();
  const { data: roomTicketProducts } = useGetBettieProducts({
    assetType: 'bettie_chat_room_ticket'
  });
  const { data: bundleProducts } = useGetBettieProducts({
    category: 'bundle'
  });

  const { data: user, refetch: refetchUser } = useUserQuery();
  const { isLoading: isLoadingUserIdentities } = useGetUserIdentities();

  // 선택된 번들 상품
  const [selectedProduct, setSelectedProduct] = useState<BettieProduct | null>(null);

  useEffect(() => {
    refetchUser();
    const onFocus = () => {
      refetchUser();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [refetchUser]);

  if (!roomTicketProducts?.length || !user) return null;

  // 방 생성권 아이템
  const productItem = roomTicketProducts[0];

  const handleClick = () => {
    if (isLoadingUserIdentities) return;
    const storedBuyerInfo = loadBillingBuyerInfo();
    const hasIdentity =
      Boolean(user?.identified) || isBillingBuyerInfoComplete(storedBuyerInfo);
    const amount = Number(productItem.baseAmount - productItem.discountAmount);

    if (selectedProduct) {
      const productName = `${productItem.name},${selectedProduct.name}`;
      const bundleAmount = Number(selectedProduct.baseAmount - selectedProduct.discountAmount);
      if (hasIdentity) {
        openNewTab(
          `/payment/billing?amount=${bundleAmount}&productName=${productName}&productId=${selectedProduct.bettieProductId}`
        );
      } else {
        openNewTab(
          `/payment/buyer-info?amount=${bundleAmount}&productName=${productName}&productId=${selectedProduct.bettieProductId}&isBilling=true`
        );
      }
    }
    if (hasIdentity) {
      openNewTab(
        `/payment/inicis?amount=${amount}&productName=${productItem.name}&productId=${productItem.bettieProductId}`
      );
    } else {
      openNewTab(
        `/payment/buyer-info?amount=${amount}&productName=${productItem.name}&productId=${productItem.bettieProductId}`
      );
    }
    onClose();
  };

  const handleBundleProductClick = (product: BettieProduct) => {
    if (selectedProduct?.bettieProductId === product.bettieProductId) {
      setSelectedProduct(null);
      return;
    }
    setSelectedProduct(product);
  };

  const displayAmount = (amount: string) => {
    switch (productItem.currency) {
      case 'KRW':
        return `${amount}원`;
      case 'USD':
        return `${amount}$`;
      default:
        return `${amount}원`;
    }
  };

  return (
    <Sheet isOpen={isOpen} onClose={onClose} detent="content">
      <Sheet.Container className="!rounded-t-[20px] !bg-background-light dark:!bg-background-elevated">
        <div className="px-[24px] py-[40px] flex flex-col gap-[32px]">
          <div
            className="p-[1px] rounded-[18px]"
            style={{
              background:
                BettieGradientColor.bettieProductOutline[colorMode as 'light' | 'dark'].background
            }}
          >
            <div
              className="rounded-[17px] z-10"
              style={{
                background:
                  BettieGradientColor.bettieProductFill[colorMode as 'light' | 'dark'].background
              }}
            >
              <div className="flex items-center justify-between p-[20px]">
                <div className="flex items-center gap-[6px]">
                  {bundleProducts && bundleProducts.length > 0 && (
                    <ButtonToggle type="check" size="lg" checked={true} />
                  )}
                  <p className="flex flex-col gap-[2px] items-start text-[16px] font-semibold leading-[19px] text-text-main-lv2">
                    {productItem.name}
                    <span className="text-[12px] font-normal text-text-main-lv3">
                      {t('BettieChargeBottomSheet.oneTime')}
                    </span>
                  </p>
                </div>
                {/* 선택된 상품 금액 */}
                {selectedProduct ? (
                  <div className="flex flex-col gap-[2px] items-end">
                    <p className="text-[15px] font-semibold leading-[18px] text-text-primary">
                      {displayAmount(
                        Number(
                          productItem.baseAmount - selectedProduct.discountAmount
                        ).toLocaleString()
                      )}
                    </p>
                    <p className="text-[12px] font-medium leading-[14px] text-text-sub-lv1 line-through">
                      {displayAmount(
                        Number(productItem.baseAmount - productItem.discountAmount).toLocaleString()
                      )}
                    </p>
                  </div>
                ) : (
                  <p className="text-[15px] font-semibold leading-[18px] text-text-primary">
                    {displayAmount(
                      Number(productItem.baseAmount - productItem.discountAmount).toLocaleString()
                    )}
                  </p>
                )}
              </div>
              {/* 번들 상품 아이템 */}
              {bundleProducts && bundleProducts.length > 0 && (
                <div className="flex flex-col gap-[14px] p-[16px] items-center border-t border-secondary-light dark:border-secondary-dark w-full">
                  <p className="text-[13px] font-semibold leading-[16px] bg-gradient-single-red bg-clip-text text-transparent">
                    {t('BettieChargeBottomSheet.purchaseWithBundle')}
                  </p>
                  <div className="flex flex-col gap-[5px] w-full">
                    {bundleProducts.map((bundleProduct) => {
                      const [title, description] = bundleProduct.name.split('/');
                      return (
                        <button
                          type="button"
                          key={bundleProduct.bettieProductId}
                          onClick={() => handleBundleProductClick(bundleProduct)}
                          className="bg-secondary-light dark:bg-secondary-dark rounded-[12px] p-[16px] flex items-center justify-between w-full"
                        >
                          <div className="flex items-center gap-[7px]">
                            {selectedProduct?.bettieProductId === bundleProduct.bettieProductId ? (
                              <ButtonToggle
                                type="check"
                                size="lg"
                                checked={
                                  selectedProduct?.bettieProductId === bundleProduct.bettieProductId
                                }
                              />
                            ) : (
                              <WhiteCheckCircle className="w-[24px] h-[24px]" />
                            )}
                            <p className="flex flex-col gap-[2px] items-start text-[15px] font-semibold leading-[18px] text-text-main-lv2">
                              {title.trim()}

                              <span className="text-[12px] font-normal text-text-main-lv3">
                                {description?.trim() && `${description.trim()} · `}
                                {t('BettieChargeBottomSheet.monthly')}
                              </span>
                            </p>
                          </div>
                          <p className="text-[15px] font-semibold leading-[18px] text-text-primary">
                            {displayAmount(Number(bundleProduct.recurringAmount).toLocaleString())}
                            <span className="text-[12px] font-normal ml-[2px]">/월</span>
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          <BettieAgreementSection />
        </div>
        <Button
          size="large"
          className="mx-[24px] rounded-[18px] text-[17px] font-semibold leading-[20px]"
          onClick={handleClick}
        >
          {t('BettieChargeBottomSheet.agreeAndPay', {
            price: selectedProduct
              ? `${Number(selectedProduct.baseAmount - selectedProduct.discountAmount).toLocaleString()}원`
              : `${Number(productItem.baseAmount - productItem.discountAmount).toLocaleString()}원`
          })}
        </Button>
      </Sheet.Container>
      <Sheet.Backdrop onClick={onClose} className="!bg-[rgba(0,0,0,0.50)]" />
    </Sheet>
  );
};

export default BettieCreateRoomTicketBottomSheet;
